export type DistributePoint<T extends { id: string }> = {
  item: T
  value: number
  groupKey?: string
}

export type LayoutPoint<T extends { id: string }> = {
  item: T
  items: T[]
  value: number
  groupKey?: string
}

export type PositionedPoint<T extends { id: string }> = LayoutPoint<T> & {
  x: number
  y: number
}

export type DistributeItemsOptions<T extends { id: string }> = {
  points: DistributePoint<T>[]
  size: number
  padding: number
  direction: 'horizontal' | 'vertical'
  width: number
  height: number
  startPercent: number
  rangePercent: number
  collapseOverflow?: boolean
}

const mapValuePercent = (value: number, startPercent: number, rangePercent: number) =>
  startPercent + value * rangePercent

const alongPosition = (
  value: number,
  startPercent: number,
  rangePercent: number,
  length: number,
  size: number,
) => (mapValuePercent(value, startPercent, rangePercent) / 100) * length - size / 2

const spreadPositions = (count: number, spreadLength: number, size: number, padding: number) => {
  const max = Math.max(0, spreadLength - size)
  if (count <= 1) return [max / 2]

  const paddedSpan = count * size + (count - 1) * padding
  if (paddedSpan <= spreadLength) {
    const start = (spreadLength - paddedSpan) / 2
    return Array.from({ length: count }, (_, i) => start + i * (size + padding))
  }

  return Array.from({ length: count }, (_, i) => (i / (count - 1)) * max)
}

const compareIds = (a: string, b: string) => a.localeCompare(b)

const pointsToLayout = <T extends { id: string }>(
  points: DistributePoint<T>[],
  options: {
    size: number
    padding: number
    direction: 'horizontal' | 'vertical'
    width: number
    height: number
    collapseOverflow: boolean
  },
): LayoutPoint<T>[] => {
  const { size, padding, direction, width, height, collapseOverflow } = options

  if (!collapseOverflow) {
    return points.map((point) => ({
      item: point.item,
      items: [point.item],
      value: point.value,
      groupKey: point.groupKey,
    }))
  }

  const isHorizontal = direction === 'horizontal'
  const spreadLength = isHorizontal ? height : width
  if (spreadLength <= 0) {
    return points.map((point) => ({
      item: point.item,
      items: [point.item],
      value: point.value,
      groupKey: point.groupKey,
    }))
  }

  const capacity = Math.max(1, Math.floor((spreadLength + padding) / (size + padding)))

  const groups = new Map<string, DistributePoint<T>[]>()
  for (const point of points) {
    const key = `${point.value}:${point.groupKey ?? ''}`
    const group = groups.get(key) ?? []
    group.push(point)
    groups.set(key, group)
  }

  const layout: LayoutPoint<T>[] = []
  for (const group of groups.values()) {
    if (group.length > capacity) {
      layout.push({
        item: group[0]!.item,
        items: group.map((point) => point.item),
        value: group[0]!.value,
        groupKey: group[0]!.groupKey,
      })
      continue
    }
    for (const point of group) {
      layout.push({
        item: point.item,
        items: [point.item],
        value: point.value,
        groupKey: point.groupKey,
      })
    }
  }
  return layout
}

const maxCliqueSize = <T extends { id: string }>(
  items: LayoutPoint<T>[],
  along: (point: LayoutPoint<T>) => number,
  minDistance: number,
) => {
  if (items.length <= 1) return items.length
  const sorted = [...items].sort((a, b) => along(a) - along(b) || compareIds(a.item.id, b.item.id))
  let max = 1
  let left = 0
  for (let right = 0; right < sorted.length; right++) {
    while (along(sorted[right]!) - along(sorted[left]!) >= minDistance) left++
    max = Math.max(max, right - left + 1)
  }
  return max
}

const overflowWindow = <T extends { id: string }>(
  items: LayoutPoint<T>[],
  along: (point: LayoutPoint<T>) => number,
  minDistance: number,
  capacity: number,
) => {
  const sorted = [...items].sort((a, b) => along(a) - along(b) || compareIds(a.item.id, b.item.id))
  let left = 0
  for (let right = 0; right < sorted.length; right++) {
    while (along(sorted[right]!) - along(sorted[left]!) >= minDistance) left++
    if (right - left + 1 > capacity) {
      return sorted.slice(left, right + 1)
    }
  }
  return null
}

const pickMergePair = <T extends { id: string }>(window: LayoutPoint<T>[]) => {
  let best: [LayoutPoint<T>, LayoutPoint<T>] | null = null
  let bestScore = Infinity

  for (let i = 0; i < window.length; i++) {
    for (let j = i + 1; j < window.length; j++) {
      const a = window[i]!
      const b = window[j]!
      const sameGroup = (a.groupKey ?? '') === (b.groupKey ?? '') ? 0 : 1
      const valueGap = Math.abs(a.value - b.value)
      const score = sameGroup * 1000 + valueGap
      if (score < bestScore) {
        bestScore = score
        best = compareIds(a.item.id, b.item.id) <= 0 ? [a, b] : [b, a]
      }
    }
  }
  return best
}

const collapseToCapacity = <T extends { id: string }>(
  items: LayoutPoint<T>[],
  along: (point: LayoutPoint<T>) => number,
  minDistance: number,
  capacity: number,
) => {
  const result = items.map((point) => ({ ...point, items: [...point.items] }))
  while (true) {
    const window = overflowWindow(result, along, minDistance, capacity)
    if (!window) break
    const pair = pickMergePair(window)
    if (!pair) break
    const [keep, drop] = pair
    keep.items.push(...drop.items)
    result.splice(result.indexOf(drop), 1)
  }
  return result
}

const connectedComponents = <T extends { id: string }>(
  items: LayoutPoint<T>[],
  along: (point: LayoutPoint<T>) => number,
  minDistance: number,
) => {
  const parent = items.map((_, i) => i)
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]!]!
      i = parent[i]!
    }
    return i
  }
  const union = (i: number, j: number) => {
    const a = find(i)
    const b = find(j)
    if (a !== b) parent[a] = b
  }

  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (Math.abs(along(items[i]!) - along(items[j]!)) < minDistance) {
        union(i, j)
      }
    }
  }

  const groups = new Map<number, LayoutPoint<T>[]>()
  items.forEach((item, i) => {
    const root = find(i)
    const group = groups.get(root) ?? []
    group.push(item)
    groups.set(root, group)
  })
  return [...groups.values()]
}

const assignLanes = <T extends { id: string }>(
  items: LayoutPoint<T>[],
  along: (point: LayoutPoint<T>) => number,
  lanes: number[],
  minDistance: number,
) => {
  const occupants: LayoutPoint<T>[][] = lanes.map(() => [])
  const preferred = lanes
    .map((_, index) => index)
    .sort(
      (a, b) =>
        Math.abs(a - (lanes.length - 1) / 2) - Math.abs(b - (lanes.length - 1) / 2) || a - b,
    )

  const sorted = [...items].sort((a, b) => along(a) - along(b) || compareIds(a.item.id, b.item.id))
  const assigned = new Map<LayoutPoint<T>, number>()

  for (const item of sorted) {
    const itemAlong = along(item)
    const lane =
      preferred.find((index) =>
        occupants[index]!.every((other) => Math.abs(along(other) - itemAlong) >= minDistance),
      ) ??
      preferred.reduce((best, index) => {
        const conflicts = occupants[index]!.filter(
          (other) => Math.abs(along(other) - itemAlong) < minDistance,
        ).length
        const bestConflicts = occupants[best]!.filter(
          (other) => Math.abs(along(other) - itemAlong) < minDistance,
        ).length
        return conflicts < bestConflicts ? index : best
      }, preferred[0]!)

    occupants[lane]!.push(item)
    assigned.set(item, lanes[lane]!)
  }

  return assigned
}

export const positionPoints = <T extends { id: string }>(
  options: DistributeItemsOptions<T>,
): PositionedPoint<T>[] => {
  const {
    points,
    size,
    padding,
    direction,
    width,
    height,
    startPercent,
    rangePercent,
    collapseOverflow = false,
  } = options

  const layout = pointsToLayout(points, {
    size,
    padding,
    direction,
    width,
    height,
    collapseOverflow,
  })

  const minDistance = size + padding
  const isHorizontal = direction === 'horizontal'
  const length = isHorizontal ? width : height
  const spreadLength = isHorizontal ? height : width
  const along = (point: LayoutPoint<T>) =>
    alongPosition(point.value, startPercent, rangePercent, length, size)

  const capacity = Math.max(1, Math.floor((spreadLength + padding) / (size + padding)))
  let working = layout
  if (collapseOverflow && spreadLength > 0) {
    working = collapseToCapacity(layout, along, minDistance, capacity)
  }

  const spreadByPoint = new Map<LayoutPoint<T>, number>()
  for (const component of connectedComponents(working, along, minDistance)) {
    const clique = maxCliqueSize(component, along, minDistance)
    const laneCount = Math.max(1, clique)
    const lanes = spreadPositions(laneCount, spreadLength, size, padding)
    const assigned = assignLanes(component, along, lanes, minDistance)
    assigned.forEach((spread, point) => spreadByPoint.set(point, spread))
  }

  return working.map((point) => {
    const alongValue = along(point)
    const spread = spreadByPoint.get(point) ?? 0
    return {
      ...point,
      x: isHorizontal ? alongValue : spread,
      y: isHorizontal ? spread : alongValue,
    }
  })
}
